import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, FileText, X, Stethoscope, Mail, ShieldCheck, Quote } from "lucide-react";
import ClinicalOutput from "../components/ClinicalOutput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEV_COLOR = {
  CRITICAL: "bg-rose-100 text-rose-800 border-rose-200",
  HIGH: "bg-orange-100 text-orange-800 border-orange-200",
  MODERATE: "bg-amber-100 text-amber-800 border-amber-200",
  LOW: "bg-slate-100 text-slate-700 border-slate-200",
};

// Reusable read-only field row used by the Triage view inside the modal.
function TriageField({ label, value, mono }) {
  const filled = value && String(value).trim().length > 0;
  return (
    <div className="py-2 border-b border-slate-100 last:border-0">
      <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{label}</div>
      <div className={`mt-0.5 text-sm ${filled ? "text-slate-900" : "text-slate-400 italic"} ${mono ? "font-mono text-xs" : ""} whitespace-pre-wrap`}>
        {filled ? value : "Not provided"}
      </div>
    </div>
  );
}

// ─── Single case row ──────────────────────────────────────────────────────
function CaseRow({ c, onOpen }) {
  return (
    <div
      className="flex items-center justify-between gap-3 px-4 py-3 rounded-md border border-slate-200 bg-white hover:border-slate-300 transition"
      data-testid={`dvb-case-row-${c.case_id}`}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Badge variant="outline" className={`shrink-0 text-[10px] font-bold border ${SEV_COLOR[c.expected_min_severity] || SEV_COLOR.LOW}`}>
          {c.expected_min_severity}
        </Badge>
        <Badge variant="outline" className="shrink-0 text-[10px] font-bold border bg-slate-50 text-slate-700 border-slate-200">
          {c.specialty}
        </Badge>
        <span className="truncate text-sm text-slate-800 font-medium">{c.title}</span>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={() => onOpen(c)}
        className="shrink-0 border-slate-300 hover:border-slate-900 hover:bg-slate-900 hover:text-white transition"
        data-testid={`dvb-view-soap-${c.case_id}`}
      >
        <FileText className="w-3.5 h-3.5 mr-1.5" />
        View SOAP
      </Button>
    </div>
  );
}

// ─── Modal that runs a case live via /api/intake ─────────────────────────
function CaseSoapDialog({ open, onClose, caseObj }) {
  const [phase, setPhase] = useState("idle");
  const [result, setResult] = useState(null);
  const [errMsg, setErrMsg] = useState(null);
  // View mode toggle (added May 20, 2026 — Devarajan/Jay/Jayanthi requested S-only default;
  // full SOAP still available on physician click).
  const [viewModeChoice, setViewModeChoice] = useState("triage");

  useEffect(() => {
    if (!open || !caseObj) return;
    let cancelled = false;
    setPhase("running");
    setResult(null);
    setErrMsg(null);
    const payload = {
      ...caseObj.intake,
      communicationMode: "text",
      viewMode: "physician",
      consentGiven: true,
    };
    axios.post(`${API}/intake`, payload, { headers: { "x-system-test": "pai-system-test-2026" } })
      .then(res => {
        if (cancelled) return;
        setResult(res.data);
        setPhase("done");
      })
      .catch(err => {
        if (cancelled) return;
        setErrMsg(err.response?.data?.error || err.message || "Pipeline error.");
        setPhase("error");
      });
    return () => { cancelled = true; };
  }, [open, caseObj]);

  if (!caseObj) return null;
  const intake = caseObj.intake || {};

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0" data-testid="dvb-soap-dialog">
        <DialogHeader className="sticky top-0 bg-white z-10 border-b border-slate-200 px-6 py-4">
          <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-700" />
            {caseObj.title}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Case ID: {caseObj.case_id} · Specialty: {caseObj.specialty} · Fictional case · No PHI
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 space-y-5">
          {/* Case Information */}
          <section data-testid="dvb-case-info">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Case Information</h3>
            <div className="rounded-md bg-slate-50 border border-slate-200 p-4 text-sm grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><span className="font-semibold text-slate-600">Age</span><div className="text-slate-900">{intake.age || "—"}</div></div>
              <div><span className="font-semibold text-slate-600">Sex</span><div className="text-slate-900">{intake.sex || "—"}</div></div>
              <div><span className="font-semibold text-slate-600">Language</span><div className="text-slate-900">{intake.language || "English"}</div></div>
              <div className="sm:col-span-3"><span className="font-semibold text-slate-600">Chief Complaint</span><div className="text-slate-900 whitespace-pre-wrap">{intake.chiefComplaint || "—"}</div></div>
              {intake.symptoms && (
                <div className="sm:col-span-3"><span className="font-semibold text-slate-600">Symptoms</span><div className="text-slate-900 whitespace-pre-wrap">{intake.symptoms}</div></div>
              )}
              {intake.medications && (
                <div className="sm:col-span-3"><span className="font-semibold text-slate-600">Medications</span><div className="text-slate-900 whitespace-pre-wrap">{intake.medications}</div></div>
              )}
              {intake.existingConditions && (
                <div className="sm:col-span-3"><span className="font-semibold text-slate-600">Conditions / PMH</span><div className="text-slate-900 whitespace-pre-wrap">{intake.existingConditions}</div></div>
              )}
              {intake.notes && (
                <div className="sm:col-span-3"><span className="font-semibold text-slate-600">Notes</span><div className="text-slate-900 whitespace-pre-wrap">{intake.notes}</div></div>
              )}
            </div>
          </section>

          {/* SOAP output */}
          <section data-testid="dvb-soap-output">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Clinical Output</h3>
              {phase === "done" && (
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">View:</label>
                  <Select value={viewModeChoice} onValueChange={setViewModeChoice}>
                    <SelectTrigger className="h-8 w-[220px] text-xs" data-testid="dvb-view-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="triage" data-testid="dvb-view-triage">History + Subjective Only</SelectItem>
                      <SelectItem value="full" data-testid="dvb-view-full">Full SOAP Note</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {phase === "running" && (
              <div className="flex items-center gap-2 text-sm text-slate-600 py-8 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" />
                Running deterministic pipeline…
              </div>
            )}
            {phase === "error" && (
              <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                {errMsg || "Unable to run this case. Please close and try again."}
              </div>
            )}
            {phase === "done" && result && viewModeChoice === "full" && (
              <ClinicalOutput result={result} viewMode="physician" />
            )}
            {phase === "done" && result && viewModeChoice === "triage" && (
              <div data-testid="dvb-triage-view">
                {/* History block */}
                <div className="rounded-md bg-slate-50 border border-slate-200 px-4 py-2 mb-3">
                  <TriageField label="Past medical history" value={intake.existingConditions} />
                  <TriageField label="Family history" value={intake.familyHistory} />
                  <TriageField label="Allergies" value={intake.allergies} />
                  <TriageField label="Medications" value={intake.medications} mono />
                  <TriageField label="Additional notes" value={intake.notes} />
                </div>
                {/* Risk alerts only — no diagnosis/assessment/plan/ICD */}
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Deterministic Risk Alerts</h4>
                {(result.riskFlags || []).length === 0 ? (
                  <div className="text-sm text-slate-500 italic px-4 py-3 border border-slate-200 rounded-md bg-slate-50">
                    No engine alerts triggered for this case.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(result.riskFlags || []).map((f, i) => (
                      <div key={i} className="rounded-md border border-slate-200 bg-white px-4 py-3" data-testid={`dvb-triage-alert-${i}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <Badge variant="outline" className={`text-[10px] font-bold border ${SEV_COLOR[f.severity] || SEV_COLOR.LOW}`}>{f.severity}</Badge>
                          <span className="text-sm font-bold text-slate-900">{f.category}</span>
                        </div>
                        {Array.isArray(f.triggers) && f.triggers.length > 0 && (
                          <div className="text-[11px] text-slate-600 mt-1">
                            <span className="font-semibold uppercase tracking-wide text-slate-500">Triggered by:</span>{" "}
                            <code className="bg-slate-100 px-1 py-0.5 rounded text-slate-700">{f.triggers.slice(0, 5).join(" · ")}</code>
                          </div>
                        )}
                      </div>
                    ))}
                    <p className="text-[11px] text-slate-500 italic mt-3">
                      Alerts are surfaced for clinical attention only. Diagnosis, differential, and management remain entirely with the physician. Switch to <strong>Full SOAP Note</strong> above to see the deterministic engine's complete output if you wish.
                    </p>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-3 flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="dvb-soap-close" size="sm">
            <X className="w-3.5 h-3.5 mr-1.5" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────
export default function DoctorValidatorBankPage() {
  const { slug } = useParams();
  const [bank, setBank] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [openCase, setOpenCase] = useState(null);

  useEffect(() => {
    axios.get(`/validator-banks/${slug}.json`)
      .then(res => setBank(res.data))
      .catch(err => setLoadError(`No validator bank found for "${slug}". (${err.message})`));
  }, [slug]);

  const openSoap = useCallback((c) => setOpenCase(c), []);
  const closeSoap = useCallback(() => setOpenCase(null), []);
  const [letterOpen, setLetterOpen] = useState(false);

  if (loadError) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10" data-testid="dvb-not-found">
        <div className="max-w-2xl mx-auto rounded-md border border-rose-200 bg-rose-50 px-6 py-5 text-sm text-rose-800">
          {loadError}
        </div>
      </div>
    );
  }

  if (!bank) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10 flex items-center justify-center text-slate-600 text-sm" data-testid="dvb-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading validator bank…
      </div>
    );
  }

  const doctor = bank.doctor || {};

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6" data-testid="doctor-validator-bank-page">
      <div className="max-w-4xl mx-auto">
        {/* Framing card — added May 20, 2026 — physician-respectful context above the bank */}
        <div className="rounded-lg border-2 border-slate-800 bg-slate-900 text-white p-5 mb-4 shadow-sm" data-testid="dvb-framing-card">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-cyan-300 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-[10px] font-bold tracking-widest text-cyan-300 uppercase">For Physicians &amp; Providers · Cures Act-Aligned</div>
              <h2 className="text-base font-bold tracking-tight mt-1">
                Deterministic Clinical Decision Support — modeled on Epic Best Practice Advisories &amp; Cerner rules.
              </h2>
              <p className="text-sm text-slate-300 mt-1.5 leading-relaxed">
                P.AI Global Health does <strong className="text-white">not</strong> interpret the patient's history. It <strong className="text-white">assembles</strong> it — across primary care, specialists, ER visits, foreign hospitals, and pharmacies that likely never lived together inside any single EMR your practice can see. Every alert is deterministic Python, audit-trail per flag. <strong className="text-white">No LLM in the decision loop.</strong> Diagnosis, differential, and management remain entirely with you.
              </p>
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setLetterOpen(true)}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-cyan-300 hover:text-cyan-100 underline underline-offset-2"
                  data-testid="dvb-open-letter-btn"
                >
                  <Quote className="w-3.5 h-3.5" />
                  Read the founder's letter →
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Founder's letter modal */}
        <Dialog open={letterOpen} onOpenChange={setLetterOpen}>
          <DialogContent className="max-w-2xl" data-testid="dvb-letter-modal">
            <DialogHeader>
              <DialogTitle className="text-base font-bold text-slate-900">A note from the founder</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">Sadasivam Eniasivam · P.AI Global Health LLC</DialogDescription>
            </DialogHeader>
            <div className="px-1 py-4 text-sm text-slate-800 leading-relaxed space-y-3" style={{fontFamily: 'Georgia, "Times New Roman", serif'}}>
              <p>Dear Doctor,</p>
              <p>
                I'd like your sign-off on a new <strong>deterministic Clinical Decision Support layer</strong> designed for physician review. The architecture mirrors Epic Best Practice Advisories and Cerner rules — but every rule is deterministic Python, audit-trail per flag, no large language model anywhere in the decision loop.
              </p>
              <p>
                <strong>Dr. V. N. Devarajan</strong> (Internal Medicine + Nephrology, 44 years' practice) has already reviewed 18 cases. I'd be honored to have your eyes on a similar set scoped to your specialty.
              </p>
              <p className="text-xs text-slate-500 italic">Patent Pending. Pre-YC, pre-funding, solo-built. Your feedback shapes the next iteration.</p>
              <p>Thank you for considering.</p>
              <p className="mt-2"><strong>Sivam (Sadasivam Eniasivam) · P.AI Global Health LLC</strong></p>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setLetterOpen(false)} data-testid="dvb-letter-close">
                <X className="w-3.5 h-3.5 mr-1.5" /> Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Doctor card */}
        <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50/60 p-5 mb-6 shadow-sm" data-testid="dvb-doctor-card">
          <div className="flex items-start gap-3">
            <Stethoscope className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <h1 className="text-xl font-bold text-emerald-900">{doctor.name}</h1>
              <p className="text-sm text-emerald-800 mt-0.5">{doctor.credentials}</p>
              <p className="text-sm text-emerald-700 mt-1">{doctor.affiliation}</p>
              <p className="text-xs text-emerald-700 mt-0.5">{doctor.experience}</p>
              {doctor.email && (
                <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1.5"><Mail className="w-3 h-3" />{doctor.email}</p>
              )}
              {!doctor.email && doctor.email_status && (
                <p className="text-xs text-emerald-700 italic mt-1">{doctor.email_status}</p>
              )}
            </div>
            <Badge variant="outline" className="bg-emerald-600 text-white border-emerald-700 text-[10px] font-bold uppercase tracking-wider shrink-0">
              Clinical Validator
            </Badge>
          </div>
        </div>

        <div className="mb-4">
          <h2 className="text-lg font-bold text-slate-900">Validator Bank — {bank.cases.length} cases</h2>
          <p className="text-sm text-slate-600 mt-1">{bank.intro}</p>
        </div>

        {bank.cases.length === 0 && (
          <div className="rounded-md border-2 border-dashed border-amber-300 bg-amber-50 px-5 py-6 mb-6" data-testid="dvb-empty-state">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
              <div>
                <h3 className="text-base font-bold text-amber-900">Cases pending</h3>
                <p className="text-sm text-amber-800 mt-1">
                  This dedicated validator bank for {doctor.name || "this validator"} is awaiting case selection.
                  Please reply to the founder's email with 3–5 case vignettes from your specialty (chief complaint + symptoms is enough — full SOAPs will be auto-generated by the deterministic engines).
                </p>
                <p className="text-xs text-amber-700 italic mt-2">
                  Reference format (Dr. V. N. Devarajan's 18 reviewed cases):
                  {" "}<a className="underline hover:text-amber-900" href="/validator-bank/devarajan">/validator-bank/devarajan</a>
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2 mb-6">
          {/* Dear Doctor letter box — added May 20, 2026 per founder request.
              Prominent double-lined slate border, sits directly above cases list. */}
          <div
            className="rounded-lg border-double border-4 border-slate-800 bg-white px-5 py-4 mb-4 shadow-sm"
            data-testid="dvb-dear-doctor-box"
            style={{fontFamily: 'Georgia, "Times New Roman", serif'}}
          >
            <p className="text-sm text-slate-900 leading-relaxed">Dear Doctor,</p>
            <p className="text-sm text-slate-800 leading-relaxed mt-2">
              I'd like your clinical validation of SOAP notes on a new deterministic Clinical Decision Support layer designed for physician review during physician triage. Not HIPAA-compliant. This is a synthetic-data sandbox. The production architecture is designed to be HIPAA-Eligible.
            </p>
            <p className="text-sm text-slate-800 leading-relaxed mt-2">
              The architecture mirrors Epic Best Practice Advisories and Cerner rules — but every rule is deterministic Python, audit-trail per flag, no large language model anywhere in the decision loop.
            </p>
            <p className="text-sm text-slate-800 leading-relaxed mt-2">
              Dr. V. N. Devarajan (Internal Medicine + Nephrology, 44 years' practice) has already reviewed 18 cases. I'd be honored to have your eyes on a similar set scoped to your specialty.
            </p>
            <p className="text-sm text-slate-800 leading-relaxed mt-2">
              Patent Pending. Your feedback shapes the next iteration. Thank you for considering.
            </p>
            <p className="text-sm font-bold text-slate-900 leading-relaxed mt-3">
              Sivam (Sadasivam Eniasivam) · P.Ai Global Health LLC
            </p>
          </div>
          {bank.cases.map(c => <CaseRow key={c.case_id} c={c} onOpen={openSoap} />)}
        </div>
      </div>

      <CaseSoapDialog open={!!openCase} onClose={closeSoap} caseObj={openCase} />
    </div>
  );
}
